import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Download, 
  Share2, 
  TrendingUp, 
  Clock, 
  Target,
  CheckCircle,
  AlertCircle,
  BarChart3,
  PieChart,
  Activity,
  Award,
  Phone,
  Mail,
  MapPin,
  Star,
  ChevronRight
} from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

interface SGAReportData {
  candidate: {
    id: string;
    name: string;
    email: string;
    phone: string;
    location: string;
    currentRole: string;
    experience: string;
    skills: string[];
    avatar?: string;
  };
  job: {
    title: string;
    company: string;
    location: string;
    type: string;
    level: string;
    salary: string;
  };
  report: {
    id: string;
    generatedAt: string;
    status: 'completed' | 'processing' | 'failed';
    overallScore: number;
    skillMatch: number;
    culturalFit: number;
    experienceMatch: number;
    communicationScore: number;
    recommendation: 'strong_hire' | 'hire' | 'consider' | 'no_hire';
    strengths: string[];
    concerns: string[];
    interviewQuestions: string[];
    salaryRecommendation: {
      min: number;
      max: number;
      currency: string;
    };
    nextSteps: string[];
  };
  metrics: {
    totalCalls: number;
    avgCallDuration: number;
    responseRate: number;
    engagementScore: number;
    followUpRequired: boolean;
    lastInteraction: string;
  };
  timeline: {
    date: string;
    event: string;
    status: 'completed' | 'pending' | 'cancelled';
    notes?: string;
  }[];
}

const SGAReportPage: React.FC = () => {
  const { candidateId } = useParams<{ candidateId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState<SGAReportData | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'details' | 'timeline' | 'recommendations'>('overview');

  // Generate hypothetical data
  const generateHypotheticalData = (): SGAReportData => ({
    candidate: {
      id: candidateId || 'cand_123',
      name: 'Sarah Johnson',
      email: 'sarah.johnson@email.com',
      phone: '+1 (555) 123-4567',
      location: 'San Francisco, CA',
      currentRole: 'Senior Software Engineer',
      experience: '8 years',
      skills: ['React', 'TypeScript', 'Node.js', 'AWS', 'Docker', 'Kubernetes', 'Python', 'PostgreSQL'],
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face'
    },
    job: {
      title: 'Lead Full Stack Developer',
      company: 'TechCorp Solutions',
      location: 'San Francisco, CA',
      type: 'Full-time',
      level: 'Senior',
      salary: '$120,000 - $150,000'
    },
    report: {
      id: 'sga_789',
      generatedAt: '2024-01-15T10:30:00Z',
      status: 'completed',
      overallScore: 87,
      skillMatch: 92,
      culturalFit: 85,
      experienceMatch: 90,
      communicationScore: 88,
      recommendation: 'strong_hire',
      strengths: [
        'Excellent technical skills matching job requirements',
        'Strong leadership experience in previous roles',
        'Proven track record of delivering complex projects',
        'Great cultural fit with team dynamics',
        'Strong communication and collaboration skills'
      ],
      concerns: [
        'Salary expectations may be on the higher end',
        'Currently has competing offers from other companies',
        'Relocation timeline needs clarification'
      ],
      interviewQuestions: [
        'Can you walk me through your experience leading a team of 5+ developers?',
        'How do you approach technical debt management in large codebases?',
        'Describe a challenging project where you had to learn new technologies quickly.',
        'How do you ensure code quality and maintainability in your team?',
        'What\'s your approach to mentoring junior developers?'
      ],
      salaryRecommendation: {
        min: 125000,
        max: 145000,
        currency: 'USD'
      },
      nextSteps: [
        'Schedule technical interview with engineering team',
        'Conduct cultural fit assessment with HR',
        'Prepare salary negotiation strategy',
        'Arrange follow-up call with candidate',
        'Complete reference checks'
      ]
    },
    metrics: {
      totalCalls: 4,
      avgCallDuration: 28,
      responseRate: 95,
      engagementScore: 89,
      followUpRequired: true,
      lastInteraction: '2024-01-14T15:45:00Z'
    },
    timeline: [
      {
        date: '2024-01-10T09:00:00Z',
        event: 'Initial screening call',
        status: 'completed',
        notes: 'Strong initial impression, very responsive'
      },
      {
        date: '2024-01-12T14:30:00Z',
        event: 'Technical assessment',
        status: 'completed',
        notes: 'Excellent problem-solving skills, quick learner'
      },
      {
        date: '2024-01-14T15:45:00Z',
        event: 'Team fit interview',
        status: 'completed',
        notes: 'Great cultural alignment, collaborative approach'
      },
      {
        date: '2024-01-16T10:00:00Z',
        event: 'Final interview with CTO',
        status: 'pending',
        notes: 'Scheduled for next week'
      }
    ]
  });

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setReportData(generateHypotheticalData());
      setLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, [candidateId]);

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-500';
    if (score >= 80) return 'text-blue-500';
    if (score >= 70) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getRecommendationColor = (recommendation: string) => {
    switch (recommendation) {
      case 'strong_hire': return 'bg-green-500/20 text-green-700 dark:text-green-300 border-green-500/30';
      case 'hire': return 'bg-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-500/30';
      case 'consider': return 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-300 border-yellow-500/30';
      case 'no_hire': return 'bg-red-500/20 text-red-700 dark:text-red-300 border-red-500/30';
      default: return 'bg-background-300/20 dark:bg-background-dark-300/20 text-text-secondary dark:text-text-secondary border-border-200 dark:border-border-dark-200';
    }
  };

  const getRecommendationText = (recommendation: string) => {
    switch (recommendation) {
      case 'strong_hire': return 'Strong Hire';
      case 'hire': return 'Hire';
      case 'consider': return 'Consider';
      case 'no_hire': return 'No Hire';
      default: return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background-50 via-white to-background-100 dark:from-background-dark-200 dark:via-surface-dark-100 dark:to-background-dark-200 flex items-center justify-center">
        <div className="text-center space-y-4">
          <LoadingSpinner size="xl" variant="ai" className="mx-auto" />
          <p className="text-lg text-text-secondary dark:text-text-secondary">Generating SGA Report...</p>
        </div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background-50 via-white to-background-100 dark:from-background-dark-200 dark:via-surface-dark-100 dark:to-background-dark-200 flex items-center justify-center">
        <div className="text-center space-y-4">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto" />
          <p className="text-lg text-text-secondary dark:text-text-secondary">Failed to load report data</p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }

  return (
      <div className="min-h-screen bg-gradient-to-br from-background-50 via-white to-background-100 dark:from-background-dark-200 dark:via-surface-dark-100 dark:to-background-dark-200">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-green-500/5 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      <div className="relative z-10 p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="hover:bg-white/20 transition-all duration-300"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  SGA Report
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Comprehensive candidate analysis and recommendations
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button variant="outline" size="sm" className="glass-card hover:glass-card-hover">
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
              <Button size="sm" className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600">
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </div>
          </div>

          {/* Candidate & Job Info */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Candidate Card */}
            <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl p-6 hover:shadow-3xl transition-all duration-500">
              <div className="flex items-start space-x-4">
                <div className="relative">
                  <img
                    src={reportData.candidate.avatar}
                    alt={reportData.candidate.name}
                    className="w-16 h-16 rounded-full object-cover ring-4 ring-white/20"
                  />
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                    <CheckCircle className="w-3 h-3 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-text-primary dark:text-text-primary mb-2">
                    {reportData.candidate.name}
                  </h3>
                  <p className="text-text-secondary dark:text-text-secondary mb-3">{reportData.candidate.currentRole}</p>
                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-text-secondary dark:text-text-secondary">
                      <Mail className="w-4 h-4 mr-2" />
                      {reportData.candidate.email}
                    </div>
                    <div className="flex items-center text-sm text-text-secondary dark:text-text-secondary">
                      <Phone className="w-4 h-4 mr-2" />
                      {reportData.candidate.phone}
                    </div>
                    <div className="flex items-center text-sm text-text-secondary dark:text-text-secondary">
                      <MapPin className="w-4 h-4 mr-2" />
                      {reportData.candidate.location}
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Job Card */}
            <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl p-6 hover:shadow-3xl transition-all duration-500">
              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-text-primary dark:text-text-primary mb-2">
                    {reportData.job.title}
                  </h3>
                  <p className="text-text-secondary dark:text-text-secondary mb-3">{reportData.job.company}</p>
                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-text-secondary dark:text-text-secondary">
                      <MapPin className="w-4 h-4 mr-2" />
                      {reportData.job.location}
                    </div>
                    <div className="flex items-center text-sm text-text-secondary dark:text-text-secondary">
                      <Clock className="w-4 h-4 mr-2" />
                      {reportData.job.type} • {reportData.job.level}
                    </div>
                    <div className="flex items-center text-sm text-text-secondary dark:text-text-secondary">
                      <Award className="w-4 h-4 mr-2" />
                      {reportData.job.salary}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Overall Score */}
          <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl p-8 mb-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 mb-6 relative">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 animate-pulse opacity-75"></div>
                <div className="relative z-10">
                  <div className="text-4xl font-bold text-white">
                    {reportData.report.overallScore}
                  </div>
                  <div className="text-white/80 text-sm">Overall Score</div>
                </div>
              </div>
              <div className={`inline-flex items-center px-4 py-2 rounded-full text-lg font-semibold ${getRecommendationColor(reportData.report.recommendation)}`}>
                {getRecommendationText(reportData.report.recommendation)}
              </div>
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="flex space-x-1 bg-white/10 dark:bg-gray-800/10 rounded-xl p-1 backdrop-blur-sm">
            {[
              { id: 'overview', label: 'Overview', icon: BarChart3 },
              { id: 'details', label: 'Details', icon: PieChart },
              { id: 'timeline', label: 'Timeline', icon: Activity },
              { id: 'recommendations', label: 'Recommendations', icon: Target }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'bg-white dark:bg-surface-dark-100 shadow-lg text-blue-600 dark:text-blue-400'
                    : 'text-text-secondary dark:text-text-secondary hover:text-text-primary dark:hover:text-text-primary hover:bg-white/5'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-8">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Score Breakdown */}
              <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                  <BarChart3 className="w-6 h-6 mr-3 text-blue-500" />
                  Score Breakdown
                </h3>
                <div className="space-y-4">
                  {[
                    { label: 'Skill Match', score: reportData.report.skillMatch, color: 'blue' },
                    { label: 'Cultural Fit', score: reportData.report.culturalFit, color: 'green' },
                    { label: 'Experience Match', score: reportData.report.experienceMatch, color: 'purple' },
                    { label: 'Communication', score: reportData.report.communicationScore, color: 'orange' }
                  ].map((item) => (
                    <div key={item.label} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-text-secondary dark:text-text-secondary">{item.label}</span>
                        <span className={`font-bold ${getScoreColor(item.score)}`}>{item.score}%</span>
                      </div>
                      <div className="w-full bg-background-200 dark:bg-background-dark-200 rounded-full h-3 overflow-hidden">
                        <div
                          className={`h-full bg-gradient-to-r from-${item.color}-400 to-${item.color}-600 rounded-full transition-all duration-1000 ease-out`}
                          style={{ width: `${item.score}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Key Metrics */}
              <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                  <Activity className="w-6 h-6 mr-3 text-green-500" />
                  Key Metrics
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Total Calls', value: reportData.metrics.totalCalls, icon: Phone, color: 'blue' },
                    { label: 'Avg Duration', value: `${reportData.metrics.avgCallDuration}m`, icon: Clock, color: 'green' },
                    { label: 'Response Rate', value: `${reportData.metrics.responseRate}%`, icon: TrendingUp, color: 'purple' },
                    { label: 'Engagement', value: `${reportData.metrics.engagementScore}%`, icon: Star, color: 'orange' }
                  ].map((metric, _index) => (
                    <div key={metric.label} className="text-center p-4 rounded-xl bg-white/5 dark:bg-gray-800/5 hover:bg-white/10 dark:hover:bg-gray-800/10 transition-all duration-300">
                      <metric.icon className={`w-8 h-8 mx-auto mb-2 text-${metric.color}-500`} />
                      <div className="text-2xl font-bold text-text-primary dark:text-text-primary">{metric.value}</div>
                      <div className="text-sm text-text-secondary dark:text-text-secondary">{metric.label}</div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}

          {activeTab === 'details' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Strengths */}
              <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                  <CheckCircle className="w-6 h-6 mr-3 text-green-500" />
                  Strengths
                </h3>
                <div className="space-y-3">
                  {reportData.report.strengths.map((strength, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 rounded-lg bg-green-50/50 dark:bg-green-900/20 border border-green-200/50 dark:border-green-700/50">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-text-secondary dark:text-text-secondary">{strength}</span>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Concerns */}
              <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                  <AlertCircle className="w-6 h-6 mr-3 text-yellow-500" />
                  Areas of Concern
                </h3>
                <div className="space-y-3">
                  {reportData.report.concerns.map((concern, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 rounded-lg bg-yellow-50/50 dark:bg-yellow-900/20 border border-yellow-200/50 dark:border-yellow-700/50">
                      <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                      <span className="text-text-secondary dark:text-text-secondary">{concern}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}

          {activeTab === 'timeline' && (
            <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                <Activity className="w-6 h-6 mr-3 text-blue-500" />
                Interaction Timeline
              </h3>
              <div className="space-y-4">
                {reportData.timeline.map((event, index) => (
                  <div key={index} className="flex items-start space-x-4 p-4 rounded-xl bg-white/5 dark:bg-gray-800/5 hover:bg-white/10 dark:hover:bg-gray-800/10 transition-all duration-300">
                    <div className={`w-3 h-3 rounded-full mt-2 ${
                      event.status === 'completed' ? 'bg-green-500' :
                      event.status === 'pending' ? 'bg-yellow-500' : 'bg-red-500'
                    }`}></div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-gray-900 dark:text-white">{event.event}</h4>
                        <span className="text-sm text-text-tertiary dark:text-text-tertiary">
                          {new Date(event.date).toLocaleDateString()}
                        </span>
                      </div>
                      {event.notes && (
                        <p className="text-text-secondary dark:text-text-secondary mt-1">{event.notes}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {activeTab === 'recommendations' && (
            <div className="space-y-8">
              {/* Interview Questions */}
              <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                  <Target className="w-6 h-6 mr-3 text-purple-500" />
                  Recommended Interview Questions
                </h3>
                <div className="space-y-4">
                  {reportData.report.interviewQuestions.map((question, index) => (
                    <div key={index} className="p-4 rounded-lg bg-purple-50/50 dark:bg-purple-900/20 border border-purple-200/50 dark:border-purple-700/50">
                      <div className="flex items-start space-x-3">
                        <div className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">
                          {index + 1}
                        </div>
                        <p className="text-text-secondary dark:text-text-secondary">{question}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Next Steps */}
              <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                  <ChevronRight className="w-6 h-6 mr-3 text-green-500" />
                  Next Steps
                </h3>
                <div className="space-y-3">
                  {reportData.report.nextSteps.map((step, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 rounded-lg bg-green-50/50 dark:bg-green-900/20 border border-green-200/50 dark:border-green-700/50">
                      <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {index + 1}
                      </div>
                      <span className="text-text-secondary dark:text-text-secondary">{step}</span>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Salary Recommendation */}
              <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                  <Award className="w-6 h-6 mr-3 text-yellow-500" />
                  Salary Recommendation
                </h3>
                <div className="text-center">
                  <div className="text-4xl font-bold text-text-primary dark:text-text-primary mb-2">
                    ${reportData.report.salaryRecommendation.min.toLocaleString()} - ${reportData.report.salaryRecommendation.max.toLocaleString()}
                  </div>
                  <p className="text-text-secondary dark:text-text-secondary">Based on market analysis and candidate profile</p>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SGAReportPage;
